import { useState } from "react";
import { Link } from "react-router-dom";
import ResellerSidebar from "@/pages/reseller/components/ResellerSidebar";
import { mockResellerUser } from "@/mocks/reseller";
import { mockBrowseProducts } from "@/mocks/resellerBrowse";

const platformIcons: Record<string, string> = {
  TikTok: "ri-tiktok-line",
  YouTube: "ri-youtube-line",
  Instagram: "ri-instagram-line",
  Douyin: "ri-tiktok-line",
  WeChat: "ri-wechat-line",
  Twitter: "ri-twitter-x-line",
};

const warehouseFlag: Record<string, string> = {
  USA: "🇺🇸",
  Canada: "🇨🇦",
  Both: "🌎",
};

type BrowseProduct = typeof mockBrowseProducts[0];

const ResellerBrowsePage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedWarehouse, setSelectedWarehouse] = useState("All");
  const [sortBy, setSortBy] = useState("commission");
  const [generatedLink, setGeneratedLink] = useState<{ product: BrowseProduct; url: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [promotingIds, setPromotingIds] = useState<Set<string>>(new Set());

  const categories = ["All", "Garden", "Farm Products", "Food", "Home Goods", "Electronics", "Fashion", "Beauty", "Sports"];

  const filtered = mockBrowseProducts
    .filter((p) => {
      const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.factory.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCat = selectedCategory === "All" || p.category === selectedCategory;
      const matchWh = selectedWarehouse === "All" || p.warehouse === selectedWarehouse || p.warehouse === "Both";
      return matchSearch && matchCat && matchWh;
    })
    .sort((a, b) => {
      if (sortBy === "commission") return b.commission - a.commission;
      if (sortBy === "price_high") return b.price - a.price;
      if (sortBy === "price_low") return a.price - b.price;
      if (sortBy === "popular") return b.totalSales - a.totalSales;
      return 0;
    });

  const generateLink = (product: BrowseProduct) => {
    const url = "https://crownprimemall.com/p/" + product.slug + "?ref=" + mockResellerUser.referralCode;
    setGeneratedLink({ product, url });
    setPromotingIds((prev) => new Set(prev).add(product.id));
  };

  const copyLink = (url: string, id: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ResellerSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Browse Factory Products</h1>
              <p className="text-xs text-gray-400">Pick products to promote and generate your referral links</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-[#D4AF37]/10 px-3 py-2 rounded-lg">
            <i className="ri-links-line text-[#D4AF37] text-sm"></i>
            <span className="text-xs font-bold text-[#B8860B]">Code: {mockResellerUser.referralCode}</span>
          </div>
        </header>

        <main className="flex-1 p-6">
          {/* Info Banner */}
          <div className="bg-gradient-to-r from-[#1a0a0a] to-[#3a1010] rounded-xl p-5 mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/20 rounded-xl flex-shrink-0">
                <i className="ri-store-2-line text-[#D4AF37] text-2xl"></i>
              </div>
              <div>
                <div className="text-white font-bold text-sm">Earn Commission on Every Sale</div>
                <div className="text-white/60 text-xs mt-0.5">
                  Each factory sets their own commission rate. Click &ldquo;Promote&rdquo; to get your unique referral link — share it on TikTok, Douyin, Instagram, YouTube, or WeChat.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              {["TikTok", "Douyin", "Instagram", "YouTube", "WeChat"].map((p) => (
                <div key={p} className="flex items-center gap-1 bg-white/10 px-2.5 py-1 rounded-full">
                  <i className={platformIcons[p] + " text-xs text-white"}></i>
                  <span className="text-[10px] text-white/80">{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products or factories..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#C41E3A] transition-colors"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#C41E3A] cursor-pointer bg-white"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#C41E3A] cursor-pointer bg-white"
              >
                <option value="All">All Warehouses</option>
                <option value="USA">USA Only</option>
                <option value="Canada">Canada Only</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#C41E3A] cursor-pointer bg-white"
              >
                <option value="commission">Highest Commission</option>
                <option value="popular">Most Popular</option>
                <option value="price_high">Price: High to Low</option>
                <option value="price_low">Price: Low to High</option>
              </select>
            </div>
          </div>

          <div className="text-xs text-gray-400 mb-4">{filtered.length} products available</div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product) => {
              const isPromoting = promotingIds.has(product.id);
              const commissionAmount = ((product.price * product.commission) / 100).toFixed(2);
              return (
                <div
                  key={product.id}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#D4AF37]/50 transition-all group"
                >
                  {/* Product Image */}
                  <div className="relative w-full h-48 overflow-hidden bg-gray-50">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Commission Badge */}
                    <div className="absolute top-2 left-2 bg-[#C41E3A] text-white text-xs font-bold px-2 py-1 rounded-full">
                      {product.commission}% commission
                    </div>
                    {/* Warehouse Badge */}
                    <div className="absolute top-2 right-2 bg-white/90 text-xs font-semibold px-2 py-1 rounded-full">
                      {warehouseFlag[product.warehouse]} {product.warehouse}
                    </div>
                    {isPromoting && (
                      <div className="absolute bottom-2 left-2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill text-xs"></i>
                        Promoting
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    <Link
                      to={"/reseller/factory/" + product.factorySlug}
                      className="text-xs text-[#C41E3A] hover:underline mb-1 truncate block cursor-pointer"
                    >
                      <i className="ri-store-2-line mr-1"></i>{product.factory}
                    </Link>
                    <div className="text-sm font-semibold text-gray-900 leading-tight mb-2 line-clamp-2">{product.name}</div>

                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-base font-bold text-gray-900">${product.price.toFixed(2)}</span>
                        <div className="text-[10px] text-gray-400">per unit</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-[#C41E3A]">${commissionAmount}</div>
                        <div className="text-[10px] text-gray-400">you earn / sale</div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-3 mb-3 text-[10px] text-gray-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-shopping-cart-line"></i>
                        {product.totalSales.toLocaleString()} sold
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400"></i>
                        {product.rating}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {product.activeResellers} resellers
                      </span>
                    </div>

                    {/* Category tag */}
                    <div className="mb-3">
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{product.category}</span>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => generateLink(product)}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                        isPromoting
                          ? "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                          : "bg-[#C41E3A] text-white hover:bg-[#a01830]"
                      }`}
                    >
                      <i className={isPromoting ? "ri-links-line" : "ri-add-circle-line"}></i>
                      {isPromoting ? "Get Link Again" : "Promote & Get Link"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-2xl">
                <i className="ri-store-2-line text-3xl"></i>
              </div>
              <p className="text-sm font-semibold">No products found</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          )}
        </main>
      </div>

      {/* Generated Link Modal */}
      {generatedLink && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-xl">
                  <i className="ri-links-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Your Referral Link is Ready!</h2>
                  <p className="text-xs text-gray-400">Share this link to earn commission</p>
                </div>
              </div>
              <button
                onClick={() => setGeneratedLink(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Product Preview */}
              <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <img src={generatedLink.product.image} alt={generatedLink.product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 truncate">{generatedLink.product.factory}</div>
                  <div className="text-sm font-semibold text-gray-900 leading-tight mt-0.5 line-clamp-2">{generatedLink.product.name}</div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-sm font-bold text-gray-900">${generatedLink.product.price.toFixed(2)}</span>
                    <span className="text-xs font-bold text-[#C41E3A] bg-[#C41E3A]/10 px-2 py-0.5 rounded-full">
                      {generatedLink.product.commission}% commission
                    </span>
                  </div>
                </div>
              </div>

              {/* Commission Breakdown */}
              <div className="bg-[#fdf8f0] border border-[#D4AF37]/30 rounded-xl p-4">
                <div className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-[#D4AF37]"></i>
                  Commission Breakdown
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-base font-bold text-gray-900">${generatedLink.product.price.toFixed(2)}</div>
                    <div className="text-[10px] text-gray-400">Product Price</div>
                  </div>
                  <div className="border-x border-[#D4AF37]/20">
                    <div className="text-base font-bold text-[#D4AF37]">{generatedLink.product.commission}%</div>
                    <div className="text-[10px] text-gray-400">Your Rate</div>
                  </div>
                  <div>
                    <div className="text-base font-bold text-[#C41E3A]">
                      ${((generatedLink.product.price * generatedLink.product.commission) / 100).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-gray-400">You Earn / Sale</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#D4AF37]/20 text-xs text-gray-500 flex items-center gap-1.5">
                  <i className="ri-paypal-line text-[#003087]"></i>
                  Commission paid to your PayPal on the 1st of every month
                </div>
              </div>

              {/* Link Box */}
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">Your Unique Referral Link</div>
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <div className="flex-1 text-xs text-gray-600 truncate font-mono">{generatedLink.url}</div>
                  <button
                    onClick={() => copyLink(generatedLink.url, generatedLink.product.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      copiedId === generatedLink.product.id
                        ? "bg-green-600 text-white"
                        : "bg-[#C41E3A] text-white hover:bg-[#a01830]"
                    }`}
                  >
                    <i className={copiedId === generatedLink.product.id ? "ri-checkbox-circle-line" : "ri-file-copy-line"}></i>
                    {copiedId === generatedLink.product.id ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>

              {/* Share Platforms */}
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">Share on Your Platforms</div>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { name: "TikTok", icon: "ri-tiktok-line", color: "bg-black text-white" },
                    { name: "Douyin", icon: "ri-tiktok-line", color: "bg-[#010101] text-white" },
                    { name: "Instagram", icon: "ri-instagram-line", color: "bg-gradient-to-br from-[#f09433] to-[#bc1888] text-white" },
                    { name: "YouTube", icon: "ri-youtube-line", color: "bg-red-600 text-white" },
                    { name: "WeChat", icon: "ri-wechat-line", color: "bg-green-600 text-white" },
                  ].map((p) => (
                    <button
                      key={p.name}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-semibold cursor-pointer transition-opacity hover:opacity-80 ${p.color}`}
                    >
                      <i className={p.icon + " text-lg"}></i>
                      <span className="text-[10px]">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setGeneratedLink(null)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Done — Browse More Products
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerBrowsePage;
